/**
 * In-memory {@link OutboxStore} implementation.
 *
 * Serves two purposes:
 *  - the default store when no persistence is wired (degraded, non-durable —
 *    survives only the page session);
 *  - the canonical test double — adversarial tests drive it directly.
 *
 * A production app injects an adapter backed by `@norkron/web-offline-store`
 * (IndexedDB) so the outbox survives a tab close / reload. The
 * {@link OutboxStore} contract is intentionally tiny so that adapter is trivial.
 */

import type { OutboxEntry, OutboxStore, ProgressSnapshot } from './types'

export class MemoryOutboxStore implements OutboxStore {
	private rows = new Map<number, OutboxEntry>()
	private seq = 0

	async add(entry: Omit<OutboxEntry, 'id'>): Promise<number> {
		const id = ++this.seq
		this.rows.set(id, { ...entry, id })
		return id
	}

	async listPending(limit: number): Promise<OutboxEntry[]> {
		const pending = [...this.rows.values()]
			.filter((r) => r.status === 'pending')
			.sort((a, b) => a.id - b.id)
		return pending.slice(0, Math.max(0, limit)).map((r) => ({ ...r }))
	}

	async listInFlight(): Promise<OutboxEntry[]> {
		return [...this.rows.values()]
			.filter((r) => r.status === 'in_flight')
			.sort((a, b) => a.id - b.id)
			.map((r) => ({ ...r }))
	}

	async update(id: number, patch: Partial<OutboxEntry>): Promise<void> {
		const cur = this.rows.get(id)
		if (!cur) return
		this.rows.set(id, { ...cur, ...patch, id })
	}

	async counts(): Promise<ProgressSnapshot> {
		const snap: ProgressSnapshot = {
			pending: 0,
			in_flight: 0,
			succeeded: 0,
			failed: 0,
		}
		for (const r of this.rows.values()) {
			if (r.status === 'pending') snap.pending++
			else if (r.status === 'in_flight') snap.in_flight++
			else if (r.status === 'succeeded') snap.succeeded++
			else if (r.status === 'failed') snap.failed++
		}
		return snap
	}

	async pruneSucceeded(): Promise<number> {
		// Collect ids first, then delete — avoids mutating the Map mid-iteration.
		const doomed: number[] = []
		for (const r of this.rows.values()) {
			if (r.status === 'succeeded') doomed.push(r.id)
		}
		for (const id of doomed) this.rows.delete(id)
		return doomed.length
	}

	/** Test helper: total row count regardless of status. */
	size(): number {
		return this.rows.size
	}

	/** Test helper: snapshot all rows. */
	all(): OutboxEntry[] {
		return [...this.rows.values()].map((r) => ({ ...r }))
	}
}
